/**
 * Endereços do painel antigo (favoritos, links salvos) e para onde vão agora.
 *
 * Cada caminho antigo (`/violacoes` ou `/legado/violacoes`) abre a página nova
 * correspondente, mantendo os parâmetros do endereço. Páginas com abas recebem
 * `?aba=` para já abrir na aba certa.
 *
 * `/visao` era a escolha de visão do protótipo, que não existe mais: abre a
 * visão da pessoa (favoritos e a volta do login podem ainda apontar para ela).
 *
 * O link dos comentários do GitLab (`/?redirect=chamado&wid=`) não passa por
 * aqui: ele é tratado antes, na raiz do app (ver `chamado-redirect.ts`).
 */
import { inicioDoPerfil, perfilDaPessoa, perfilPreferido } from '@/lib/visoes'

interface Destino {
  pagina: string
  aba?: string
  /** Pedaço do endereço depois da página (ex.: `labels-isentas` em `/admin/projetos/labels-isentas`). */
  sub?: string
}

/** Caminho antigo (sem `/legado`) → página nova. `/chamado` continua igual. */
const MAPA: Record<string, Destino> = {
  '/issues_estagnadas': { pagina: 'tarefas-paradas' },
  '/violacoes_usuario': { pagina: 'meus-alertas' },
  '/regras': { pagina: 'regras' },
  '/violacoes': { pagina: 'alertas' },
  '/webhooks': { pagina: 'projetos' },
  '/milestone_dashboard': { pagina: 'entregas', aba: 'milestones' },
  '/merge_por_epico': { pagina: 'entregas', aba: 'mrs' },
  '/julgamentos_wiki': { pagina: 'qualidade', aba: 'wikis' },
  '/julgamentos_qa': { pagina: 'qualidade', aba: 'comentarios' },
  '/notas_qa': { pagina: 'qualidade', aba: 'nota' },
  '/metricas_de_qa': { pagina: 'qualidade', aba: 'nota' },
  '/gerenciar_usuarios': { pagina: 'pessoas', aba: 'acessos' },
  '/vinculo_emails_commit': { pagina: 'pessoas', aba: 'emails' },
  '/correlacao_projetos_times': { pagina: 'pessoas', aba: 'times' },
  '/configuracao_grupos_e_projetos': { pagina: 'projetos', sub: 'configurar' },
  '/configuracoes_globais': { pagina: 'configuracoes' },
  '/responsaveis_email': { pagina: 'pessoas', aba: 'resumo' },
  '/custos_llm': { pagina: 'uso-ia' },
  '/labels_isentas': { pagina: 'projetos', sub: 'labels-isentas' },
  '/erros': { pagina: 'erros' },
}

/** Caminho sem o prefixo `/legado` (`/legado/violacoes` → `/violacoes`). */
function semPrefixoLegado(caminho: string): string {
  return caminho.replace(/^\/legado(?=\/|$)/, '') || '/'
}

/**
 * True se o caminho é de uma tela do painel antigo (ex.: `/violacoes`), com ou
 * sem `/legado` na frente.
 */
export function ehRotaAntiga(caminho: string): boolean {
  return semPrefixoLegado(caminho) in MAPA
}

/** Página nova que substitui a tela antiga (`/violacoes` ou `/legado/violacoes`). */
export function destinoDaTelaAntiga(caminho: string): Destino | undefined {
  const semLegado = semPrefixoLegado(caminho)
  if (semLegado === '/') return { pagina: 'inicio' }
  return MAPA[semLegado]
}

/**
 * Endereço novo para um caminho antigo, com os parâmetros mantidos.
 * Devolve `null` se o caminho não é de uma tela antiga.
 */
export function enderecoNovo(
  caminho: string,
  search: string,
  papeis: ReadonlySet<string>
): string | null {
  if (caminho.replace(/\/+$/, '') === '/visao') {
    return `${inicioDoPerfil(perfilDaPessoa(papeis), papeis)}${search}`
  }
  if (!ehRotaAntiga(caminho)) return null
  const destino = destinoDaTelaAntiga(caminho)
  if (!destino) return null
  const perfil = perfilPreferido(papeis, destino.pagina)
  const parametros = new URLSearchParams(search)
  if (destino.aba && !parametros.has('aba')) parametros.set('aba', destino.aba)
  const query = parametros.toString()
  const sub = destino.sub ? `/${destino.sub}` : ''
  return `/${perfil.id}/${destino.pagina}${sub}${query ? `?${query}` : ''}`
}
